// E8QuadrilateralParallelogramRhombusPFClosedTask.js
// Zadanie zamknięte E8 (2025 CZE zad. 14) - czworokąt ABCD w układzie współrzędnych (P/F).
// S1: czy ABCD jest równoległobokiem (AB = DC)?  S2: czy dla k_test ABCD jest rombem?

import React from "react";
import { BlockMath, InlineMath } from "../../../components/math/KatexWrappers";
import TruthInput from "../../../components/inputs/TruthInput";
import GraphViewer from "../../../components/GraphPlotter/GraphViewer";

// C i D mają zawsze stałe x: Cx=3, Dx=-1 (jak w oryginale).
// Zmieniają się A, B i k_test.
const Cx = 3, Dx = -1;

const CASES = [
  { Ax: -3, Ay: -2, Bx: 1,  By: -2, k_test: 3 }, // original: S1=P, S2=F
  { Ax: -1, Ay: -1, Bx: 3,  By: -1, k_test: 3 }, // S1=P, S2=P (romb/kwadrat)
  { Ax: -3, Ay: -3, Bx: 1,  By: -3, k_test: 2 }, // S1=P, S2=F
  { Ax: -2, Ay: -2, Bx: 3,  By: -2, k_test: 3 }, // S1=F, S2=F
  { Ax: -4, Ay: -1, Bx: 0,  By: -1, k_test: 3 }, // S1=P, S2=F
  { Ax: -1, Ay: -2, Bx: 3,  By: -2, k_test: 2 }, // S1=P, S2=P
  { Ax: -5, Ay: -1, Bx: 0,  By: -1, k_test: 4 }, // S1=F, S2=F
  { Ax: -2, Ay: -4, Bx: 2,  By: -4, k_test: 1 }, // S1=P, S2=F
];

function sq(x) { return x * x; }

function buildData({ Ax, Ay, Bx, By, k_test }) {
  const abX = Bx - Ax;
  const dcLen = Cx - Dx; // = 4 zawsze
  const isParallelogram = abX === dcLen && Ay === By;

  const bcX = Cx - Bx;
  const bcY = k_test - By;
  const bc2 = sq(bcX) + sq(bcY);
  const ab2 = sq(abX);
  const isRhombus = isParallelogram && bc2 === ab2;

  return {
    Ax, Ay, Bx, By, Cx, Dx, k_test,
    abLen: abX,
    dcLen,
    bc2,
    ab2,
    isParallelogram,
    isRhombus,
    correctPF: [isParallelogram ? "P" : "F", isRhombus ? "P" : "F"],
  };
}

function createOriginalTask() {
  return {
    type: "E8QuadrilateralParallelogramRhombusPFClosedTask",
    data: buildData(CASES[0]),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 14 },
    tags: ["e8", "geometria-analityczna", "rownoleglobok", "romb", "prawda_falsz"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  const c = CASES[1 + Math.floor(Math.random() * (CASES.length - 1))];
  return {
    type: "E8QuadrilateralParallelogramRhombusPFClosedTask",
    data: buildData(c),
    source: { variant: "random", basedOn: "E8_2025_CZE_14" },
    tags: ["e8", "geometria-analityczna", "rownoleglobok", "romb", "prawda_falsz"],
  };
}

function CoordDiagram({ data }) {
  const { Ax, Ay, Bx, By } = data;
  const pointColor = "#ff9f0a";
  const points = {
    A: { x: Ax, y: Ay, color: pointColor, textPosition: "bottom left" },
    B: { x: Bx, y: By, color: pointColor, textPosition: "bottom right" },
  };
  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "14px 0", overflowX: "auto" }}>
      <GraphViewer
        points={points}
        segments={[]}
        xRange={[-6, 6]}
        yRange={[-5, 5]}
        width={380}
        height={300}
        squareFrame={false}
      />
    </div>
  );
}

function wrap(v) {
  return v < 0 ? `(${v})` : `${v}`;
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        W układzie współrzędnych <InlineMath math="(x,y)" /> zaznaczono kolejne wierzchołki{" "}
        <InlineMath math="A" /> i <InlineMath math="B" /> pewnego czworokąta{" "}
        <InlineMath math="ABCD" />. Punkty <InlineMath math="A" /> i <InlineMath math="B" /> są
        punktami kratowymi. Pozostałe wierzchołki czworokąta mają współrzędne{" "}
        <InlineMath math={`C=(${Cx},\\ y_C)`} /> oraz{" "}
        <InlineMath math={`D=(${Dx},\\ y_D)`} />, gdzie <InlineMath math="y_C" /> jest liczbą
        całkowitą dodatnią oraz <InlineMath math="y_C=y_D" />.
      </p>
      <CoordDiagram data={data} />
      <p>
        <strong>
          Oceń prawdziwość podanych zdań. Wybierz <InlineMath math="P" />, jeśli zdanie
          jest prawdziwe, albo <InlineMath math="F" /> – jeśli jest fałszywe.
        </strong>
      </p>
      <p>1. Czworokąt <InlineMath math="ABCD" /> jest równoległobokiem.</p>
      <p>
        2. Dla <InlineMath math={`y_C=y_D=${data.k_test}`} /> czworokąt{" "}
        <InlineMath math="ABCD" /> jest rombem.
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer || {};
  const setValue = (key, v) => onChange?.({ ...value, [key]: v });
  return (
    <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
      {[["s1", "1."], ["s2", "2."]].map(([key, label]) => (
        <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ minWidth: 18 }}>{label}</span>
          <TruthInput
            value={value[key] || null}
            disabled={disabled}
            onChange={(v) => setValue(key, v)}
          />
        </div>
      ))}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer?.s1 === data.correctPF[0] && userAnswer?.s2 === data.correctPF[1];
}

export function renderAnswer({ data }) {
  return (
    <InlineMath
      math={`1.\\ \\text{${data.correctPF[0]}},\\quad 2.\\ \\text{${data.correctPF[1]}}`}
    />
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer?.s1 && !userAnswer?.s2) return <i>(brak odpowiedzi)</i>;
  return (
    <InlineMath
      math={`1.\\ \\text{${userAnswer?.s1 || "-"}},\\quad 2.\\ \\text{${userAnswer?.s2 || "-"}}`}
    />
  );
}

export function renderExplanation({ data }) {
  const { Ax, Ay, Bx, By, k_test, abLen, dcLen, bc2, ab2, isParallelogram, isRhombus } = data;
  const bcX = Cx - Bx;
  const bcY = k_test - By;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Zdanie 1.</strong> Czworokąt ABCD jest równoległobokiem, gdy{" "}
        <InlineMath math="\overrightarrow{AB}=\overrightarrow{DC}" />.
      </p>
      <BlockMath
        math={`\\overrightarrow{AB}=(${Bx}-${wrap(Ax)},\\ ${By}-${wrap(Ay)})=(${abLen},\\ 0)`}
      />
      <BlockMath
        math={`\\overrightarrow{DC}=(${Cx}-${wrap(Dx)},\\ 0)=(${dcLen},\\ 0)`}
      />
      <p>
        {isParallelogram
          ? <>Wektory są równe → ABCD jest równoległobokiem. Zdanie 1. jest <strong>prawdziwe</strong>.</>
          : <>Wektory są różne (<InlineMath math={`${abLen}\\neq${dcLen}`} />) → ABCD nie jest równoległobokiem. Zdanie 1. jest <strong>fałszywe</strong>.</>
        }
      </p>
      <p>
        <strong>Zdanie 2.</strong> Dla <InlineMath math={`y_C=y_D=${k_test}`} /> sprawdzamy,
        czy <InlineMath math="|BC|=|AB|" />:
      </p>
      <BlockMath
        math={`|BC|=\\sqrt{(${Cx}-${wrap(Bx)})^2+(${k_test}-${wrap(By)})^2}=\\sqrt{${bcX * bcX}+${bcY * bcY}}=\\sqrt{${bc2}}`}
      />
      <BlockMath math={`|AB|=${abLen},\\quad |AB|^2=${ab2}`} />
      <p>
        {isRhombus
          ? <><InlineMath math={`${bc2}=${ab2}`} /> → <InlineMath math="|BC|=|AB|" /> → wszystkie boki równe → ABCD jest rombem. Zdanie 2. jest <strong>prawdziwe</strong>.</>
          : isParallelogram
            ? <><InlineMath math={`${bc2}\\neq ${ab2}`} /> → <InlineMath math="|BC|\\neq|AB|" /> → ABCD nie jest rombem. Zdanie 2. jest <strong>fałszywe</strong>.</>
            : <>ABCD nie jest nawet równoległobokiem, więc nie jest rombem. Zdanie 2. jest <strong>fałszywe</strong>.</>
        }
      </p>
      <p>
        Odpowiedź:{" "}
        <InlineMath
          math={`1.\\ \\text{${data.correctPF[0]}},\\quad 2.\\ \\text{${data.correctPF[1]}}`}
        />.
      </p>
    </div>
  );
}
